import { createSlice } from '@reduxjs/toolkit';
import {
  PaginationType,
} from '@nxsys/types/models/Common';
import { showToast } from '@nxsys/utils/toast';
import { appIntl } from '@nxsys/helpers/Common';
import { statutoryActs } from '../action';

export interface StatutoryState {
  loading: boolean;
  statutoryLeaveData: {
    list: any[];
    pagination: PaginationType;
    queryParams: any;
  };
  statutoryPaymentData: {
    list: any[];
    pagination: PaginationType;
    queryParams: any;
  };
  aweStatutory: any;
  statutoryDetail: any;
}

const initialState: StatutoryState = {
  loading: false,
  statutoryLeaveData: {
    list: [],
    pagination: {
      current: 1,
      pageSize: 20,
      totalDocs: 0,
    },
    queryParams: null,
  },
  statutoryPaymentData: {
    list: [],
    pagination: {
      current: 1,
      pageSize: 20,
      totalDocs: 0,
    },
    queryParams: null,
  },
  aweStatutory: null,
  statutoryDetail: null,
};

const statutorySlice = createSlice({
  name: 'statutory',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    // Get Statutory Leave Records
    builder.addCase(statutoryActs.getStatutoryLeaveRecords.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(statutoryActs.getStatutoryLeaveRecords.fulfilled, (state, action) => {
      state.loading = false;
      state.statutoryLeaveData.list = action.payload.data;
      state.statutoryLeaveData.pagination = action.payload.pagination;
      state.statutoryLeaveData.queryParams = (action.payload as any).queryParams;
    });
    builder.addCase(statutoryActs.getStatutoryLeaveRecords.rejected, (state, action) => {
      state.loading = false;
      showToast({
        type: 'error',
        message:
          (action.payload as any)?.response?.data?.message ||
          appIntl().formatMessage({ id: 'statutory.leave.getError' }),
      });
    });

    // Get Statutory Leave Records
    builder.addCase(statutoryActs.getStatutoryPaymentRecords.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(statutoryActs.getStatutoryPaymentRecords.fulfilled, (state, action) => {
      state.loading = false;
      state.statutoryPaymentData.list = action.payload.data;
      state.statutoryPaymentData.pagination = action.payload.pagination;
      state.statutoryPaymentData.queryParams = (action.payload as any).queryParams;
    });
    builder.addCase(statutoryActs.getStatutoryPaymentRecords.rejected, (state, action) => {
      state.loading = false;
      showToast({
        type: 'error',
        message:
          (action.payload as any)?.response?.data?.message ||
          appIntl().formatMessage({ id: 'statutory.payment.getError' }),
      });
    });

    // Get AWE
    builder.addCase(statutoryActs.getAWEStatutory.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(statutoryActs.getAWEStatutory.fulfilled, (state, action) => {
      state.loading = false;
      state.aweStatutory = action.payload.data;
    });
    builder.addCase(statutoryActs.getAWEStatutory.rejected, (state, action) => {
      state.loading = false;
      state.aweStatutory = null;
      showToast({
        type: 'error',
        message:
          (action.payload as any)?.response?.data?.message ||
          appIntl().formatMessage({ id: 'statutory.awe.getError' }),
      });
    });

    // Create Statutory
    builder.addCase(statutoryActs.createStatutory.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(statutoryActs.createStatutory.fulfilled, (state, action) => {
      state.loading = false;
      showToast({
        type: 'success',
        message:
          (action.payload as any)?.response?.data?.message ||
          appIntl().formatMessage({ id: 'statutory.create.getSuccess' }),
      });
    });
    builder.addCase(statutoryActs.createStatutory.rejected, (state, action) => {
      state.loading = false;
      showToast({
        type: 'error',
        message:
          (action.payload as any)?.response?.data?.message ||
          appIntl().formatMessage({ id: 'statutory.create.getError' }),
      });
    });

    // Delete Statutory
    builder.addCase(statutoryActs.deleteStatutory.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(statutoryActs.deleteStatutory.fulfilled, (state, action) => {
      state.loading = false;
      showToast({
        type: 'success',
        message:
          (action.payload as any)?.response?.data?.message ||
          appIntl().formatMessage({ id: 'statutory.delete.getSuccess' }),
      });
    });
    builder.addCase(statutoryActs.deleteStatutory.rejected, (state, action) => {
      state.loading = false;
      showToast({
        type: 'error',
        message:
          (action.payload as any)?.response?.data?.message ||
          appIntl().formatMessage({ id: 'statutory.delete.getError' }),
      });
    });

    // Get Statutory Detail
    builder.addCase(statutoryActs.getStatutoryDetail.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(statutoryActs.getStatutoryDetail.fulfilled, (state, action) => {
      state.loading = false;
      state.statutoryDetail = action.payload;
    });
    builder.addCase(statutoryActs.getStatutoryDetail.rejected, (state, action) => {
      state.loading = false;
      showToast({
        type: 'error',
        message:
          (action.payload as any)?.response?.data?.message ||
          appIntl().formatMessage({ id: 'statutory.detail.getError' }),
      });
    });

    // Stop Statutory Record
    builder.addCase(statutoryActs.stopStatutoryRecord.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(statutoryActs.stopStatutoryRecord.fulfilled, (state, action) => {
      state.loading = false;
      showToast({
        type: 'success',
        message:
          (action.payload as any)?.response?.data?.message ||
          appIntl().formatMessage({ id: 'statutory.stop.getSuccess' }),
      });
    });
    builder.addCase(statutoryActs.stopStatutoryRecord.rejected, (state, action) => {
      state.loading = false;
      showToast({
        type: 'error',
        message:
          (action.payload as any)?.response?.data?.message ||
          appIntl().formatMessage({ id: 'statutory.stop.getError' }),
      });
    });

    // Resume Statutory Record
    builder.addCase(statutoryActs.resumeStatutoryRecord.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(statutoryActs.resumeStatutoryRecord.fulfilled, (state, action) => {
      state.loading = false;
      showToast({
        type: 'success',
        message:
          (action.payload as any)?.response?.data?.message ||
          appIntl().formatMessage({ id: 'statutory.resume.getSuccess' }),
      });
    });
    builder.addCase(statutoryActs.resumeStatutoryRecord.rejected, (state, action) => {
      state.loading = false;
      showToast({
        type: 'error',
        message:
          (action.payload as any)?.response?.data?.message ||
          appIntl().formatMessage({ id: 'statutory.resume.getError' }),
      });
    });

    // Update Statutory Record
    builder.addCase(statutoryActs.updateStatutoryRecord.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(statutoryActs.updateStatutoryRecord.fulfilled, (state, action) => {
      state.loading = false;
      showToast({
        type: 'success',
        message:
          (action.payload as any)?.response?.data?.message ||
          appIntl().formatMessage({ id: 'statutory.record.update.getSuccess' }),
      });
    });
    builder.addCase(statutoryActs.updateStatutoryRecord.rejected, (state, action) => {
      state.loading = false;
      showToast({
        type: 'error',
        message:
          (action.payload as any)?.response?.data?.message ||
          appIntl().formatMessage({ id: 'statutory.record.update.getError' }),
      });
    });

    // Update Statutory Record By Id
    builder.addCase(statutoryActs.updateStatutoryRecordById.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(statutoryActs.updateStatutoryRecordById.fulfilled, (state) => {
      state.loading = false;
    });
    builder.addCase(statutoryActs.updateStatutoryRecordById.rejected, (state, action) => {
      state.loading = false;
      showToast({
        type: 'error',
        message:
          (action.payload as any)?.message ||
          (action.payload as any)?.response?.data?.message ||
          appIntl().formatMessage({ id: 'statutory.record.update.getError' }),
      });
    });
  },
});

export const statutoryReducer = statutorySlice.reducer;
export const statutoryActions = {
  ...statutorySlice.actions,
  ...statutoryActs,
};
